import * as googleHelpers from "../../../helpers/google-helpers";

type Position = { lat: number; lon: number };

const lookUp = async (position: Position) => {
  // convert
  const legacyResponse = await googleHelpers.reverseGeoCode(position);
  const { place, lat, lon, ...rest } = legacyResponse;
  return { placeName: place, coords: { lat, lon }, ...rest };
};

export type ReverseGeocodeResponse = Awaited<ReturnType<typeof lookUp>>;

// ~10 m. A parked truck reports slightly different coordinates on every
// refresh; rounding to 4 decimals lets those share one Google lookup without
// moving the position far enough to land on a different street number.
const COORD_DECIMALS = 4;

// Enough for every truck on the Live View several times over. Past it, the
// oldest entries are dropped (a Map iterates in insertion order), so a tab
// left open all day doesn't keep growing.
const MAX_ENTRIES = 2000;

// Lives as long as the tab: it survives the Live View refresh button and
// moving around the app, not a page reload. The pending lookup is stored, so
// trucks parked at the same spot share one Google call even when looked up
// at the same time.
const cache = new Map<string, Promise<ReverseGeocodeResponse>>();

const toCacheKey = (position: Position) =>
  `${position.lat.toFixed(COORD_DECIMALS)},${position.lon.toFixed(
    COORD_DECIMALS
  )}`;

/**
 * Coordinates -> address through Google, cached in memory per rounded
 * position. The returned coords are always the caller's own position. A
 * failed lookup isn't kept, so the next refresh asks Google again.
 */
export const reverseGeocode = async (
  position: Position
): Promise<ReverseGeocodeResponse> => {
  const key = toCacheKey(position);
  let pending = cache.get(key);
  if (!pending) {
    pending = lookUp(position);
    cache.set(key, pending);
    if (cache.size > MAX_ENTRIES) {
      const oldest = cache.keys().next().value;
      if (oldest !== undefined) {
        cache.delete(oldest);
      }
    }
    pending.catch(() => {
      // The caller gets the rejection through `await` below; this only
      // forgets the failed lookup.
      if (cache.get(key) === pending) {
        cache.delete(key);
      }
    });
  }
  const result = await pending;
  return { ...result, coords: { lat: position.lat, lon: position.lon } };
};

/** For tests: forget every cached lookup. */
export const clearReverseGeocodeCache = () => cache.clear();
